<template>
  <div class="regions-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-map-location-dot"></i>
        Regiões
      </h1>
      <p class="page-subtitle">Gerencie as regiões do sistema</p>
    </div>

    <!-- Navegação por tabs -->
    <div class="tabs-container">
      <nav class="modern-tabs">
        <button 
          class="tab-button" 
          :class="{ active: selectedTab === 1 }" 
          @click="selectedTab = 1"
        >
          <i class="fa-solid fa-list"></i>
          LISTAGEM
        </button>
        <button 
          class="tab-button" 
          :class="{ 
            active: selectedTab === 2 && !region.id, 
            disabled: !canCreateEditRegions 
          }" 
          @click="canCreateEditRegions ? createRegion() : showPermissionError()"
          :disabled="!canCreateEditRegions"
          :title="!canCreateEditRegions ? 'Você não tem permissão para criar/editar regiões' : 'Adicionar nova região'"
        >
          <i class="fa-solid fa-plus tab-plus-icon"></i>
          CADASTRO
        </button>
        <button 
          v-if="region.id"
          class="tab-button" 
          :class="{ active: selectedTab === 2 }" 
          @click="selectedTab = 2"
        >
          <i class="fa-solid fa-pen-to-square"></i>
          EDITAR
        </button>
      </nav>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando...</span>
      </div>
    </div>

    <!-- Tab 1: Listagem -->
    <div v-show="selectedTab === 1 && !loading" class="tab-content">
      <div class="table-card">
        <!-- Cabeçalho da tabela -->
        <div class="table-header">
          <div class="search-container">
            <div class="search-input-group">
              <i class="fa-solid fa-search search-icon"></i>
              <input 
                type="text" 
                v-model="quickFilter" 
                class="search-input" 
                placeholder="Pesquisar regiões..." 
                autocomplete="off"
              />
            </div>
          </div>
          <span v-if="totalRows > 0" class="table-count">({{ totalRows }} região(ões) encontrada(s))</span>
          <div v-if="totalPages > 1" class="pagination-info">
            <span>Página {{ pageIndex }} de {{ totalPages }}</span>
            <div class="pagination-controls">
              <button 
                class="btn-pagination" 
                @click="changePage(pageIndex - 1)"
                :disabled="pageIndex <= 1"
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>
              <button 
                class="btn-pagination" 
                @click="changePage(pageIndex + 1)"
                :disabled="pageIndex >= totalPages"
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>
          <button 
            type="button" 
            class="btn-primary" 
            @click="createRegion()" 
            :disabled="!canCreateEditRegions"
            :title="!canCreateEditRegions ? 'Você não tem permissão para criar regiões' : 'Adicionar nova região'"
          >
            <i class="fa-solid fa-plus"></i>
            Adicionar Região
          </button>
        </div>

        <!-- Mensagem informativa para admin (filtros do header) -->
        <div v-if="isAdmin" class="permission-info">
          <div class="info-card">
            <i class="fa-solid fa-info-circle"></i>
            <div class="info-content">
              <span v-if="currentSelectedBanca && currentSelectedRegion">
                <strong>Filtros Ativos:</strong> Exibindo a região <strong>{{ currentSelectedRegion.name }}</strong>
                da banca <strong>{{ currentSelectedBanca.name }}</strong>.
              </span>
              <span v-else-if="currentSelectedBanca && !currentSelectedRegion">
                <strong>Filtro por Banca:</strong> Exibindo todas as regiões da banca <strong>{{ currentSelectedBanca.name }}</strong>.
                Para filtrar por região específica, selecione uma região no cabeçalho.
              </span>
              <span v-else-if="!currentSelectedBanca && currentSelectedRegion">
                <strong>Filtro por Região:</strong> Exibindo a região <strong>{{ currentSelectedRegion.name }}</strong>.
              </span>
              <span v-else>
                <strong>Visualização Global:</strong> Exibindo todas as regiões do sistema.
                Para filtrar, selecione uma banca e/ou região no cabeçalho.
              </span>
            </div>
          </div>
        </div>

        <!-- Mensagem informativa para usuários com permissões limitadas -->
        <div v-if="!isAdmin" class="permission-info">
          <div class="info-card">
            <i class="fa-solid fa-info-circle"></i>
            <div class="info-content">
              <span v-if="isBanca">
                <strong>Modo BANCA:</strong> Você pode visualizar apenas as regiões onde é responsável e criar/editar regiões sendo automaticamente definido como responsável.
              </span>
              <span v-else-if="isGerente">
                <strong>Modo GERENTE:</strong> Você pode visualizar apenas as regiões onde é responsável. Criação e edição de regiões não são permitidas.
              </span>
            </div>
          </div>
        </div>

        <!-- Tabela -->
        <div class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th>Nome da Região</th>
                <th>Responsável</th>
                <th>Tipo de Região</th>
                <th>Status</th>
                <th width="300">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="r in paginatedRegions" 
                :key="r.id"
                class="table-row"
              >
                <td>
                  <div class="region-info">
                    <span class="region-name">{{ r.name }}</span>
                  </div>
                </td>
                <td>
                  <div class="seller-column">
                    <span v-if="r.responsibleName" class="seller-name" :title="r.responsibleName">
                      {{ r.responsibleName }}
                    </span>
                    <span v-else class="no-seller">Sem responsável</span>
                  </div>
                </td>
                <td>
                  <div class="region-type-column">
                    <span 
                      :class="['region-type-badge', r.onlyDescarga ? 'badge-descarga' : 'badge-normal']"
                      :title="r.onlyDescarga ? 'Esta é uma região de descarga' : 'Esta é uma região normal'"
                    >
                      {{ r.onlyDescarga ? 'Descarga' : 'Normal' }}
                    </span>
                  </div>
                </td>
                <td>
                  <div class="status-column">
                    <span :class="['status-badge', r.isActive ? 'badge-active' : 'badge-inactive']">
                      <i :class="['fa-solid', r.isActive ? 'fa-check-circle' : 'fa-times-circle']"></i>
                      {{ r.isActive ? 'Ativa' : 'Inativa' }}
                    </span>
                  </div>
                </td>
                <td>
                  <div class="action-buttons">
                    <button 
                      class="btn-action btn-list" 
                      @click="showSellersList(r)"
                      title="Ver Vendedores"
                    >
                      <i class="fa-solid fa-users"></i>
                    </button>
                    <button 
                      class="btn-action btn-edit" 
                      @click="editRegion(r)"
                      title="Editar"
                    >
                      <i class="fa-solid fa-pen-to-square"></i>
                    </button>
                    <button
                      v-if="isAdmin"
                      :class="['btn-action', r.isActive ? 'btn-warning' : 'btn-success']"
                      @click="toggleRegionActive(r)"
                      :title="r.isActive ? 'Desativar Região' : 'Ativar Região'"
                    >
                      <i :class="['fa-solid', r.isActive ? 'fa-ban' : 'fa-check']"></i>
                    </button>
                    <button
                      v-if="isAdmin"
                      class="btn-action btn-transfer"
                      @click="openTransferBancaModal(r)"
                      title="Transferir para outra Banca"
                    >
                      <i class="fa-solid fa-right-left"></i>
                    </button>
                    <button 
                      v-if="canDeleteRegions"
                      class="btn-action btn-delete" 
                      @click="deleteRegion(r.id)"
                      title="Excluir"
                    >
                      <i class="fa-solid fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredRegions.length === 0" class="no-data">
                <td colspan="5">
                  <div class="no-data-message">
                    <i class="fa-solid fa-inbox"></i>
                    <span>Nenhuma região encontrada</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPages > 1" class="pagination-footer">
          <div class="pagination-info">
            <span>Mostrando {{ startItem }}-{{ endItem }} de {{ totalRows }} resultados</span>
          </div>
          <div class="pagination-controls">
            <button 
              class="btn-pagination" 
              @click="changePage(1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(pageIndex - 1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <span class="page-info">{{ pageIndex }} / {{ totalPages }}</span>
            <button 
              class="btn-pagination" 
              @click="changePage(pageIndex + 1)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(totalPages)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab 2: Formulário -->
    <div v-show="selectedTab === 2" class="tab-content">
      <div class="form-card form-card-with-dropdowns" :class="{ 'dropdown-open': isAnyFilterDropdownOpen }">
        <div class="form-header">
          <h3 class="form-title">
            <i class="fa-solid fa-map-location-dot"></i>
            {{ region.id ? 'Editar Região' : 'Nova Região' }}
            <p class="form-subtitle">
            {{ region.id ? 'Atualize as informações da região' : 'Preencha as informações da nova região' }}
          </p>
          </h3>
        </div>

        <div class="form-body">
          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-tag"></i>
              Nome da Região
            </label>
            <input 
              class="form-input" 
              type="text" 
              v-model="region.name"
              placeholder="Digite o nome da região..."
            />
          </div>

          <!-- Banca vinculada (ADMIN usa a banca selecionada no cabeçalho) -->
          <div v-if="isAdmin && !region.id" class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-building"></i>
              Banca
            </label>
            <div v-if="currentSelectedBanca" class="banca-info">
              <i class="fa-solid fa-link"></i>
              <span>A região será vinculada à banca <strong>{{ currentSelectedBanca.name }}</strong>.</span>
            </div>
            <div v-else class="banca-info banca-info-warning">
              <i class="fa-solid fa-triangle-exclamation"></i>
              <span>Selecione uma banca no cabeçalho (ou um responsável com banca) para vincular a região.</span>
            </div>
          </div>

          <div class="input-group" :class="{ 'dropdown-field-open': showResponsibleList }">
            <label class="input-label">
              <i class="fa-solid fa-user-tie"></i>
              Responsável
            </label>
            
            <!-- Mensagem informativa para BANCA -->
            <div v-if="isBanca" class="banca-info">
              <i class="fa-solid fa-info-circle"></i>
              <span>Como usuário BANCA, você será automaticamente definido como responsável por esta região.</span>
            </div>
            
            <!-- Select com filtro -->
            <div class="select-with-filter-container" :class="{ 'is-open': showResponsibleList }">
              <!-- Input de filtro -->
              <div class="filter-input-wrapper">
                <i class="fa-solid fa-search filter-icon"></i>
              <input 
                  v-if="!isBanca"
                type="text" 
                  v-model="responsibleFilterTerm"
                  class="form-input filter-input" 
                  placeholder="Filtrar responsáveis..."
                  @focus="showResponsibleList = true"
                  @blur="hideResponsibleList"
                autocomplete="off"
              />
                <input 
                  v-else
                  type="text" 
                  :value="currentUser.name"
                  class="form-input filter-input" 
                  readonly
                  disabled
                />
              </div>

              <!-- Lista de responsáveis filtrados -->
              <div 
                v-if="showResponsibleList && !isBanca" 
                class="responsibles-list"
              >
                <div 
                  v-if="loadingResponsibles" 
                  class="list-loading"
                >
                  <i class="fa-solid fa-spinner fa-spin"></i>
                  <span>Carregando...</span>
              </div>

              <div 
                  v-else-if="filteredResponsibles.length === 0" 
                  class="list-empty"
              >
                  <i class="fa-solid fa-user-slash"></i>
                <span>Nenhum responsável encontrado</span>
              </div>

                <div 
                  v-else
                  class="responsibles-items"
                >
              <div 
                    v-for="responsible in filteredResponsibles" 
                    :key="responsible.id"
                    class="responsible-item"
                    :class="{ 'selected': region.responsibleId === responsible.id }"
                    @mousedown="selectResponsible(responsible)"
                  >
                    <div class="responsible-avatar">
                      <i class="fa-solid fa-user"></i>
                    </div>
                    <div class="responsible-info">
                      <span class="responsible-name">{{ responsible.name }}</span>
                      <span class="responsible-username">{{ responsible.username }}</span>
                      <span class="responsible-role-badge" :class="getRoleBadgeClass(responsible.role)">
                        {{ getRoleText(responsible.role) }}
                      </span>
                    </div>
                    <i v-if="region.responsibleId === responsible.id" class="fa-solid fa-check check-icon"></i>
                </div>
                </div>
              </div>

              <!-- Responsável selecionado (preview) -->
              <div v-if="region.responsibleId && selectedResponsible" class="selected-responsible-preview">
                <i class="fa-solid fa-user-check"></i>
                <span>{{ selectedResponsible.name }}</span>
                <span class="responsible-role-tag">{{ getRoleText(selectedResponsible.role) }}</span>
                <button 
                  v-if="!isBanca"
                  type="button" 
                  class="clear-responsible-btn"
                  @click="clearResponsible"
                  title="Limpar seleção"
                >
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="input-group input-group-spaced" :class="{ 'dropdown-field-open': showDescargaRegionList }">
            <label class="input-label">
              <i class="fa-solid fa-link"></i>
              Destino da Descarga
            </label>
            
            <!-- Select com filtro para regiões de descarga -->
            <div class="select-with-filter-container" :class="{ 'is-open': showDescargaRegionList }">
              <!-- Input de filtro -->
              <div class="filter-input-wrapper">
                <i class="fa-solid fa-search filter-icon"></i>
              <input 
                type="text" 
                  v-model="descargaRegionFilterTerm"
                  class="form-input filter-input" 
                  placeholder="Filtrar regiões de descarga..."
                  @focus="showDescargaRegionList = true"
                  @blur="hideDescargaRegionList"
                autocomplete="off"
              />
              </div>

              <!-- Lista de regiões filtradas -->
              <div 
                v-if="showDescargaRegionList" 
                class="responsibles-list"
              >
                <div 
                  v-if="loadingDescargaRegions" 
                  class="list-loading"
                >
                  <i class="fa-solid fa-spinner fa-spin"></i>
                  <span>Carregando...</span>
              </div>

              <div 
                  v-else-if="filteredDescargaRegions.length === 0" 
                  class="list-empty"
              >
                  <i class="fa-solid fa-map-location-dot"></i>
                <span>Nenhuma região encontrada</span>
              </div>

                <div 
                  v-else
                  class="responsibles-items"
                >
              <div 
                    v-for="descargaRegion in filteredDescargaRegions" 
                    :key="descargaRegion.id"
                    class="responsible-item"
                    :class="{ 'selected': region.descargaRegionId === descargaRegion.id }"
                    @mousedown="selectDescargaRegion(descargaRegion)"
              >
                    <div class="responsible-avatar">
                      <i class="fa-solid fa-map-location-dot"></i>
                    </div>
                    <div class="responsible-info">
                      <span class="responsible-name">{{ descargaRegion.name }}</span>
                      <span class="responsible-username" v-if="descargaRegion.bancaName">{{ descargaRegion.bancaName }}</span>
                      <span class="responsible-role-badge" :class="descargaRegion.onlyDescarga ? 'badge-descarga' : 'badge-normal'">
                        {{ descargaRegion.onlyDescarga ? 'Descarga' : 'Normal' }}
                      </span>
                    </div>
                    <i v-if="region.descargaRegionId === descargaRegion.id" class="fa-solid fa-check check-icon"></i>
                  </div>
              </div>
            </div>

              <!-- Região selecionada (preview) -->
              <div v-if="region.descargaRegionId && selectedDescargaRegion" class="selected-responsible-preview">
                  <i class="fa-solid fa-map-location-dot"></i>
                <span>{{ selectedDescargaRegion.name }}</span>
                <span class="responsible-role-tag" :class="selectedDescargaRegion.onlyDescarga ? 'badge-descarga' : 'badge-normal'">
                    {{ selectedDescargaRegion.onlyDescarga ? 'Descarga' : 'Normal' }}
                  </span>
                <button 
                  type="button" 
                  class="clear-responsible-btn"
                  @click="clearDescargaRegion"
                  title="Limpar seleção"
                >
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
            </div>

            <div class="field-description">
              <i class="fa-solid fa-info-circle"></i>
              <span>Selecione uma região de descarga para vincular a esta região (opcional).</span>
            </div>
          </div>

          <div class="input-group input-group-spaced">
            <label class="input-label">
              <i class="fa-solid fa-truck"></i>
              Configurações da Região
            </label>
            
            <div class="checkbox-group">
              <label class="checkbox-container">
                <input 
                  type="checkbox" 
                  v-model="onlyDescarga"
                  class="checkbox-input"
                />
                <span class="checkbox-checkmark"></span>
                <span class="checkbox-label">Esta é uma região de descarga</span>
              </label>
              <div class="checkbox-description">
                <i class="fa-solid fa-info-circle"></i>
                <span>Regiões de descarga são utilizadas para controle específico de operações de descarga.</span>
              </div>
            </div>
          </div>

          <!-- Copiar configs de loteria/modalidade de outra unidade (somente criação) -->
          <div
            v-if="!region.id && availableRegionsToCopy.length > 0"
            class="input-group input-group-spaced"
            :class="{ 'dropdown-field-open': showCopyFromRegionList }"
          >
            <label class="input-label">
              <i class="fa-solid fa-copy"></i>
              Copiar configurações da unidade
            </label>

            <div class="select-with-filter-container" :class="{ 'is-open': showCopyFromRegionList }">
              <div class="filter-input-wrapper">
                <i class="fa-solid fa-search filter-icon"></i>
                <input
                  type="text"
                  v-model="copyFromRegionFilterTerm"
                  class="form-input filter-input"
                  placeholder="Selecionar unidade de origem (opcional)..."
                  @focus="showCopyFromRegionList = true"
                  @blur="hideCopyFromRegionList"
                  autocomplete="off"
                />
              </div>

              <div v-if="showCopyFromRegionList" class="responsibles-list">
                <div v-if="filteredRegionsToCopy.length === 0" class="list-empty">
                  <i class="fa-solid fa-map-location-dot"></i>
                  <span>Nenhuma unidade encontrada</span>
                </div>

                <div v-else class="responsibles-items">
                  <div
                    v-for="sourceRegion in filteredRegionsToCopy"
                    :key="sourceRegion.id"
                    class="responsible-item"
                    :class="{ selected: copyFromRegionId === sourceRegion.id }"
                    @mousedown="selectCopyFromRegion(sourceRegion)"
                  >
                    <div class="responsible-avatar">
                      <i class="fa-solid fa-clone"></i>
                    </div>
                    <div class="responsible-info">
                      <span class="responsible-name">{{ sourceRegion.name }}</span>
                      <span class="responsible-username" v-if="sourceRegion.bancaName">{{ sourceRegion.bancaName }}</span>
                    </div>
                    <i v-if="copyFromRegionId === sourceRegion.id" class="fa-solid fa-check check-icon"></i>
                  </div>
                </div>
              </div>

              <div v-if="copyFromRegionId && selectedCopyFromRegion" class="selected-responsible-preview">
                <i class="fa-solid fa-clone"></i>
                <span>{{ selectedCopyFromRegion.name }}</span>
                <button
                  type="button"
                  class="clear-responsible-btn"
                  @click="clearCopyFromRegion"
                  title="Não copiar configurações"
                >
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
            </div>

            <div class="field-description">
              <i class="fa-solid fa-info-circle"></i>
              <span>Opcional. Ao selecionar uma unidade, a nova região receberá loterias, modalidades, cotação (Lotinha/Quininha/Seninha), limites e configurações de prêmio.</span>
            </div>
          </div>
        </div>

        <div class="form-footer">
          <button 
            type="button" 
            class="btn-primary" 
            @click="region.id ? updateRegion() : insertRegion()"
            :disabled="!canCreateEditRegions || saving"
          >
            <i :class="saving ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-save'"></i>
            {{ saving ? 'Salvando...' : (region.id ? 'Atualizar' : 'Salvar') }}
          </button>
          <button 
            type="button" 
            class="btn-secondary" 
            @click="reset()"
            :disabled="saving"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Lista de Vendedores -->
    <div v-if="showSellersModal" class="modal-overlay" @click="closeSellersModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="fa-solid fa-users"></i>
            Vendedores da Região: {{ selectedRegionForSellers?.name }}
          </h3>
          <button class="modal-close-btn" @click="closeSellersModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Loading -->
          <div v-if="loadingSellers" class="modal-loading">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <span>Carregando vendedores...</span>
          </div>

          <!-- Tabela de vendedores -->
          <div v-else-if="sellersList.length > 0" class="sellers-table-container">
            <table class="sellers-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Telefone</th>
                  <th>Ponto</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="seller in sellersList" 
                  :key="seller.id"
                  class="seller-row"
                >
                  <td>
                    <div class="seller-name-cell">
                      <div class="seller-avatar-small">
                        <i class="fa-solid fa-user"></i>
                      </div>
                      <span class="seller-name">{{ seller.name }}</span>
                    </div>
                  </td>
                  <td>
                    <span v-if="seller.phone" class="seller-phone">
                      <i class="fa-solid fa-phone"></i>
                      {{ seller.phone }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <span v-if="seller.point" class="seller-point">
                      <i class="fa-solid fa-map-marker-alt"></i>
                      {{ seller.point }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <span :class="['status-badge-small', seller.isActive ? 'badge-success' : 'badge-danger']">
                      <i class="fa-solid fa-circle"></i>
                      {{ seller.isActive ? 'Ativo' : 'Inativo' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Estado vazio -->
          <div v-else class="empty-state">
            <i class="fa-solid fa-user-slash"></i>
            <h4>Nenhum vendedor encontrado</h4>
            <p>Esta região não possui vendedores cadastrados.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="closeSellersModal">
            <i class="fa-solid fa-times"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Transferir Região para outra Banca -->
    <div v-if="showTransferBancaModal" class="modal-overlay" @click="closeTransferBancaModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="fa-solid fa-right-left"></i>
            Transferir Região de Banca
          </h3>
          <button class="modal-close-btn" @click="closeTransferBancaModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="banca-info">
            <i class="fa-solid fa-map-location-dot"></i>
            <span>
              Região <strong>{{ transferRegion?.name }}</strong>
              atualmente vinculada à banca
              <strong>{{ transferRegion?.bancaName || 'Sem banca' }}</strong>.
            </span>
          </div>

          <div class="input-group input-group-spaced">
            <label class="input-label">
              <i class="fa-solid fa-building"></i>
              Banca de Destino
            </label>
            <select v-model="transferTargetBancaId" class="form-input" :disabled="loadingTransferBancas">
              <option value="">{{ loadingTransferBancas ? 'Carregando...' : 'Selecione a banca de destino' }}</option>
              <option
                v-for="banca in transferBancasOptions"
                :key="banca.id"
                :value="banca.id"
              >
                {{ banca.name }}
              </option>
            </select>
          </div>

          <div class="banca-info">
            <i class="fa-solid fa-info-circle"></i>
            <span>
              Vendedores e cobradores desta região serão movidos junto para a banca de destino.
            </span>
          </div>

          <div class="banca-info banca-info-warning">
            <i class="fa-solid fa-triangle-exclamation"></i>
            <span>
              Ao transferir, o responsável atual (se vinculado à banca de origem) será removido da região.
              Você poderá definir um novo responsável posteriormente na edição.
            </span>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="btn-primary"
            @click="confirmTransferBanca"
            :disabled="!transferTargetBancaId || transferring"
          >
            <i :class="transferring ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-right-left'"></i>
            {{ transferring ? 'Transferindo...' : 'Transferir' }}
          </button>
          <button class="btn-secondary" @click="closeTransferBancaModal" :disabled="transferring">
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import { ROLES } from "@/services/permissions";

export default {
  data: () => ({
    quickFilter: "",
    regions: [],
    region: {},
    selectedTab: 1,
    pageIndex: 1,
    pageSize: 10,
    loading: true,
    saving: false,
    
    // Select com filtro de responsáveis
    responsibleFilterTerm: "",
    allResponsibles: [],
    selectedResponsible: null,
    showResponsibleList: false,
    loadingResponsibles: false,

    // Select com filtro de regiões de descarga
    descargaRegionFilterTerm: "",
    allDescargaRegions: [], // Cache de todas as regiões carregadas
    selectedDescargaRegion: null,
    showDescargaRegionList: false,
    loadingDescargaRegions: false,

    // Modal de vendedores
    showSellersModal: false,
    selectedRegionForSellers: null,
    sellersList: [],

    // Modal de transferência de banca
    showTransferBancaModal: false,
    transferRegion: null,
    transferTargetBancaId: "",
    transferBancas: [],
    loadingTransferBancas: false,
    transferring: false,

    // Controle de usuário e permissões
    currentUser: null,
    userResponsibleRegions: [],
    userBancaId: null,
    // Snapshot reativo da banca/região do header (localStorage sozinho não é reativo)
    selectedBancaSnapshot: null,
    selectedRegionSnapshot: null,
    ONLINE_BANCA_UUID: "e9c624bd-8403-4b8c-9ba1-ca4954833e93",
    
    // Campo para região de descarga
    onlyDescarga: false,

    // Copiar configurações de outra unidade na criação
    copyFromRegionId: null,
    selectedCopyFromRegion: null,
    copyFromRegionFilterTerm: "",
    showCopyFromRegionList: false,
    
    // Guardar responsável original ao editar (para detectar mudanças)
    originalResponsibleId: null,
  }),
  computed: {
    filteredRegions() {
      let regions = this.regions;
      
      // Aplicar filtro de regiões baseado no role do usuário
      if (this.isAdmin) {
        // ADMIN: filtrar pelo select de banca/região do header
        if (this.currentSelectedBanca) {
          regions = regions.filter(region =>
            String(region.bancaId) === String(this.currentSelectedBanca.id)
          );
        } else if (localStorage.getItem('excludeOnlineBanca') === 'true') {
          // "Todas as Bancas": excluir regiões da banca ONLINE
          regions = regions.filter(region =>
            String(region.bancaId) !== this.ONLINE_BANCA_UUID
          );
        }

        if (this.currentSelectedRegion) {
          regions = regions.filter(region =>
            String(region.id) === String(this.currentSelectedRegion.id)
          );
        }
      } else if (this.isBanca) {
        // Para BANCA: filtrar regiões onde BancaId corresponde ao userBancaId
        regions = regions.filter(region => 
          String(region.bancaId) === String(this.userBancaId)
        );

        if (this.currentSelectedRegion) {
          regions = regions.filter(region =>
            String(region.id) === String(this.currentSelectedRegion.id)
          );
        }
      } else {
        // Para GERENTE e outros: filtrar baseado no ResponsibleId
        regions = regions.filter(region => 
          this.userResponsibleRegions.includes(region.id)
        );

        if (this.currentSelectedRegion) {
          regions = regions.filter(region =>
            String(region.id) === String(this.currentSelectedRegion.id)
          );
        }
      }
      
      // Aplicar filtro de pesquisa
      if (this.quickFilter) {
        regions = regions.filter(region => 
          region.name.toUpperCase().includes(this.quickFilter.toUpperCase())
        );
      }
      
      return regions;
    },

    totalRows() {
      return this.filteredRegions.length;
    },

    totalPages() {
      return Math.ceil(this.totalRows / this.pageSize) || 0;
    },

    startItem() {
      return this.totalRows === 0 ? 0 : (this.pageIndex - 1) * this.pageSize + 1;
    },

    endItem() {
      if (this.totalRows === 0) return 0;
      return Math.min(this.pageIndex * this.pageSize, this.totalRows);
    },

    paginatedRegions() {
      const start = (this.pageIndex - 1) * this.pageSize;
      return this.filteredRegions.slice(start, start + this.pageSize);
    },

    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    currentSelectedBanca() {
      return this.selectedBancaSnapshot || this.readSelectedBancaFromStorage();
    },

    currentSelectedRegion() {
      return this.selectedRegionSnapshot || this.readSelectedRegionFromStorage();
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    canCreateEditRegions() {
      // ADMIN: acesso total
      // BANCA: pode criar/editar com auto-gravação como responsável
      // GERENTE: não pode criar/editar
      return this.isAdmin || this.isBanca;
    },

    canDeleteRegions() {
      // Apenas ADMIN pode excluir regiões
      return this.isAdmin;
    },

    filteredResponsibles() {
      let list = this.allResponsibles || [];

      // ADMIN: filtrar responsáveis pela banca selecionada no cabeçalho
      if (this.isAdmin && this.currentSelectedBanca) {
        const bancaId = String(this.currentSelectedBanca.id);
        list = list.filter(responsible => {
          const role = typeof responsible.role === 'string'
            ? parseInt(responsible.role)
            : responsible.role;
          // ADMIN pode ser responsável de qualquer região
          if (role === ROLES.ADMIN) return true;
          const responsibleBancaId = responsible.bancaId ?? responsible.BancaId;
          return responsibleBancaId != null && String(responsibleBancaId) === bancaId;
        });
      }

      if (!this.responsibleFilterTerm) {
        return list;
      }

      const searchTerm = this.responsibleFilterTerm.toLowerCase();
      return list.filter(responsible =>
        responsible.name.toLowerCase().includes(searchTerm) ||
        responsible.username.toLowerCase().includes(searchTerm)
      );
    },

    filteredDescargaRegions() {
      // Destino da Descarga: lista TODAS as regiões de descarga do sistema,
      // sem a limitação do select de região/banca do cabeçalho.
      let regions = (this.allDescargaRegions || []).filter(region =>
        region.onlyDescarga === true
      );

      // Não permitir vincular a região a ela mesma
      if (this.region?.id) {
        regions = regions.filter(region =>
          String(region.id) !== String(this.region.id)
        );
      }

      if (!this.descargaRegionFilterTerm) {
        return regions;
      }

      const searchTerm = this.descargaRegionFilterTerm.toLowerCase();
      return regions.filter(region =>
        region.name.toLowerCase().includes(searchTerm) ||
        (region.bancaName && region.bancaName.toLowerCase().includes(searchTerm))
      );
    },

    availableRegionsToCopy() {
      let regions = this.regions || [];

      if (this.isAdmin) {
        // ADMIN: mesmas regras do filtro do header (banca/região)
        if (this.currentSelectedBanca) {
          regions = regions.filter(region =>
            String(region.bancaId) === String(this.currentSelectedBanca.id)
          );
        } else if (localStorage.getItem('excludeOnlineBanca') === 'true') {
          regions = regions.filter(region =>
            String(region.bancaId) !== this.ONLINE_BANCA_UUID
          );
        }
      } else if (this.isBanca) {
        regions = regions.filter(region =>
          String(region.bancaId) === String(this.userBancaId)
        );
      } else {
        regions = regions.filter(region =>
          this.userResponsibleRegions.includes(region.id)
        );
      }

      return regions;
    },

    filteredRegionsToCopy() {
      if (!this.copyFromRegionFilterTerm) {
        return this.availableRegionsToCopy;
      }

      const searchTerm = this.copyFromRegionFilterTerm.toLowerCase();
      return this.availableRegionsToCopy.filter(region =>
        region.name.toLowerCase().includes(searchTerm) ||
        (region.bancaName && region.bancaName.toLowerCase().includes(searchTerm))
      );
    },

    isAnyFilterDropdownOpen() {
      return this.showResponsibleList || this.showDescargaRegionList || this.showCopyFromRegionList;
    },

    transferBancasOptions() {
      const currentBancaId = this.transferRegion?.bancaId
        ? String(this.transferRegion.bancaId)
        : null;
      return (this.transferBancas || []).filter(banca =>
        String(banca.id) !== currentBancaId
      );
    }
  },
  watch: {
    quickFilter() {
      this.pageIndex = 1;
    },
    totalPages(newTotal) {
      if (this.pageIndex > newTotal && newTotal > 0) {
        this.pageIndex = newTotal;
      } else if (newTotal === 0) {
        this.pageIndex = 1;
      }
    },
    currentSelectedRegion() {
      this.pageIndex = 1;
    },
    currentSelectedBanca() {
      this.pageIndex = 1;
    }
  },
  async mounted() {
    this.syncSelectedBancaSnapshot();
    this.syncSelectedRegionSnapshot();
    window.addEventListener('bancaChanged', this.onRegionOrBancaChanged);
    window.addEventListener('regionChanged', this.onRegionOrBancaChanged);
    window.addEventListener('regionAutoSelected', this.onRegionOrBancaChanged);
    window.addEventListener('storage', this.onStorageChanged);

    this.loading = true;
    try {
      await this.loadCurrentUser();
      await this.loadAllResponsibles();
      await this.loadAllDescargaRegions();
      await this.searchRegions();
    } catch (error) {
      this.loading = false;
      toast.error(handleErrorMessage(error));
    }
  },
  beforeUnmount() {
    window.removeEventListener('bancaChanged', this.onRegionOrBancaChanged);
    window.removeEventListener('regionChanged', this.onRegionOrBancaChanged);
    window.removeEventListener('regionAutoSelected', this.onRegionOrBancaChanged);
    window.removeEventListener('storage', this.onStorageChanged);
  },
  methods: {
    notifyHeaderSelectsRefresh({ bancas = true, regions = true } = {}) {
      window.dispatchEvent(new CustomEvent('headerSelectsNeedRefresh', {
        detail: { bancas, regions }
      }));
    },

    readSelectedBancaFromStorage() {
      try {
        const savedBanca = localStorage.getItem('selectedBanca');
        return savedBanca ? JSON.parse(savedBanca) : null;
      } catch {
        return null;
      }
    },

    readSelectedRegionFromStorage() {
      try {
        const savedRegion = localStorage.getItem('currentRegion');
        return savedRegion ? JSON.parse(savedRegion) : null;
      } catch {
        return null;
      }
    },

    syncSelectedBancaSnapshot() {
      this.selectedBancaSnapshot = this.readSelectedBancaFromStorage();
    },

    syncSelectedRegionSnapshot() {
      this.selectedRegionSnapshot = this.readSelectedRegionFromStorage();
    },

    onRegionOrBancaChanged(event) {
      if (event?.type === 'bancaChanged') {
        const banca = event?.detail?.banca || this.readSelectedBancaFromStorage();
        this.selectedBancaSnapshot = banca || null;
      } else {
        this.syncSelectedBancaSnapshot();
      }
      this.syncSelectedRegionSnapshot();
      this.pageIndex = 1;

      // Na criação, limpar seleções incompatíveis com a banca/região do header
      if (this.selectedTab === 2 && !this.region?.id) {
        this.clearIncompatibleCreateSelections();
      }
    },

    onStorageChanged(event) {
      if (event.key === 'selectedBanca') {
        this.syncSelectedBancaSnapshot();
        this.pageIndex = 1;
        if (this.selectedTab === 2 && !this.region?.id) {
          this.clearIncompatibleCreateSelections();
        }
      }
      if (event.key === 'currentRegion') {
        this.syncSelectedRegionSnapshot();
        this.pageIndex = 1;
      }
    },

    clearIncompatibleCreateSelections() {
      const bancaId = this.currentSelectedBanca?.id
        ? String(this.currentSelectedBanca.id)
        : null;

      // Copiar de outra região
      if (this.copyFromRegionId) {
        const stillAvailable = this.availableRegionsToCopy.some(
          region => String(region.id) === String(this.copyFromRegionId)
        );
        if (!stillAvailable) {
          this.clearCopyFromRegion();
        }
      }

      // Responsável
      if (this.selectedResponsible && bancaId && !this.isBanca) {
        const role = typeof this.selectedResponsible.role === 'string'
          ? parseInt(this.selectedResponsible.role)
          : this.selectedResponsible.role;
        const responsibleBancaId =
          this.selectedResponsible.bancaId ?? this.selectedResponsible.BancaId;

        if (role !== ROLES.ADMIN && String(responsibleBancaId) !== bancaId) {
          this.selectedResponsible = null;
          this.region.responsibleId = null;
          this.responsibleFilterTerm = "";
        }
      }
    },

    normalizeGuid(value) {
      if (value == null || value === '') return null;
      const id = typeof value === 'object' ? (value.id ?? value.Id) : value;
      if (id == null || id === '') return null;
      const normalized = String(id).trim().toLowerCase();
      if (!normalized || normalized === '00000000-0000-0000-0000-000000000000') {
        return null;
      }
      return normalized;
    },

    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
          
          // Se não for ADMIN, carregar regiões do usuário responsável  
          if (!this.isAdmin) {
            await this.loadUserResponsibleRegions();
          }

          // Buscar BancaId do usuário para todas as roles (menos ADMIN pode não ter banca)
          await this.loadUserBancaId();
        }
      } catch (error) {
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userResponsibleRegions = response.data.map(region => region.id);
        
        // Incluir a região principal também, se existir
        if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
          this.userResponsibleRegions.push(this.currentUser.regionId);
        }
      } catch (error) {
        this.userResponsibleRegions = [];
        toast.error("Erro ao carregar regiões do usuário");
      }
    },

    async loadUserBancaId() {
      try {
        if (!this.currentUser) return;
        
        const response = await mainService.getUserBancaId(this.currentUser.id);
        this.userBancaId = response.data;
        

      } catch (error) {

        this.userBancaId = null;
        // Não exibir erro pois nem todos os usuários podem ter banca
      }
    },

    async loadAllResponsibles() {
      if (this.isBanca) {
        // Para BANCA, só pode selecionar a si mesmo
        this.allResponsibles = [{
          ...this.currentUser,
          roleText: this.getRoleText(this.currentUser.role)
        }];
        return;
      }

      this.loadingResponsibles = true;
      try {
        if (this.isAdmin) {
          // ADMIN busca ADMIN, GERENTE e BANCA
          const [adminResponse, gerenteResponse, bancaResponse] = await Promise.all([
            mainService.searchUsers({ isActive: true, role: 0 }), // ADMIN
            mainService.searchUsers({ isActive: true, role: 1 }), // GERENTE
            mainService.searchUsers({ isActive: true, role: 3 })  // BANCA
          ]);

          // Combinar e remover duplicatas
          const allUsers = [
            ...(adminResponse.data || []),
            ...(gerenteResponse.data || []),
            ...(bancaResponse.data || [])
          ];
          
          this.allResponsibles = allUsers
            .filter((user, index, self) => 
              index === self.findIndex(u => u.id === user.id)
            )
            .map(user => ({
              ...user,
              roleText: this.getRoleText(user.role)
            }));
        }
      } catch (error) {
        this.allResponsibles = [];
      } finally {
        this.loadingResponsibles = false;
      }
    },

    selectResponsible(responsible) {
      this.selectedResponsible = responsible;
      this.region.responsibleId = responsible.id;
      this.responsibleFilterTerm = responsible.name;
      this.showResponsibleList = false;
    },

    clearResponsible() {
      if (this.isBanca) {
        toast.error("Você deve ser o responsável pela região!");
        return;
      }
      
      this.selectedResponsible = null;
      this.region.responsibleId = null;
      this.responsibleFilterTerm = "";
    },

    hideResponsibleList() {
      setTimeout(() => {
        this.showResponsibleList = false;
      }, 150);
    },

    createRegion() {
      if (!this.canCreateEditRegions) {
        toast.error("Você não tem permissão para criar regiões!");
        return;
      }

      this.syncSelectedBancaSnapshot();
      this.syncSelectedRegionSnapshot();

      // ADMIN precisa da banca do cabeçalho para vincular a nova região
      if (this.isAdmin && !this.currentSelectedBanca) {
        toast.error("Selecione uma banca no cabeçalho antes de criar a região!");
        return;
      }

      this.region = {};
      this.selectedResponsible = null;
      this.responsibleFilterTerm = "";
      this.showResponsibleList = false;
      this.onlyDescarga = false; // Reset checkbox
      
      // Reset campos de região de descarga
      this.selectedDescargaRegion = null;
      this.descargaRegionFilterTerm = "";
      this.showDescargaRegionList = false;

      // Reset cópia de configurações
      this.clearCopyFromRegion();
      this.showCopyFromRegionList = false;
      
      // Se for BANCA, auto-selecionar como responsável
      if (this.isBanca) {
        const bancaResponsible = this.allResponsibles.find(r => r.id === this.currentUser.id);
        if (bancaResponsible) {
          this.selectedResponsible = bancaResponsible;
        this.region.responsibleId = this.currentUser.id;
          this.responsibleFilterTerm = this.currentUser.name;
        }
      }
      
      this.selectedTab = 2;
    },
    selectCopyFromRegion(sourceRegion) {
      this.selectedCopyFromRegion = sourceRegion;
      this.copyFromRegionId = sourceRegion.id;
      this.copyFromRegionFilterTerm = sourceRegion.name;
      this.showCopyFromRegionList = false;
    },
    clearCopyFromRegion() {
      this.selectedCopyFromRegion = null;
      this.copyFromRegionId = null;
      this.copyFromRegionFilterTerm = "";
    },
    hideCopyFromRegionList() {
      setTimeout(() => {
        this.showCopyFromRegionList = false;
      }, 150);
    },
    editRegion(region) {
      if (!this.canCreateEditRegions) {
        toast.error("Você não tem permissão para editar regiões!");
        return;
      }

      this.region = { ...region };
      this.onlyDescarga = region.onlyDescarga || false; // Carregar valor do checkbox
      
      // Guardar o responsável original para detectar mudanças
      this.originalResponsibleId = region.responsibleId;

      // Cópia só na criação
      this.clearCopyFromRegion();
      this.showCopyFromRegionList = false;
      
      this.loadRegionResponsible(region);
      this.loadRegionDescargaRegion(region);
      this.selectedTab = 2;
    },
    reset() {
      this.region = {};
      this.selectedResponsible = null;
      this.responsibleFilterTerm = "";
      this.showResponsibleList = false;
      this.onlyDescarga = false; // Reset checkbox
      this.originalResponsibleId = null; // Reset responsável original
      
      // Reset campos de região de descarga
      this.selectedDescargaRegion = null;
      this.descargaRegionFilterTerm = "";
      this.showDescargaRegionList = false;

      // Reset cópia de configurações
      this.clearCopyFromRegion();
      this.showCopyFromRegionList = false;
      
      this.selectedTab = 1;
    },
    validateRegion() {
      
      if (this.region.name == null || this.region.name == "") {
        toast.error("Por favor, preencha o nome da região!");
        return false;
      }
      
      // Para BANCA, garantir que o responsável seja ele mesmo
      if (this.isBanca && (!this.selectedResponsible || this.selectedResponsible.id !== this.currentUser.id)) {
        const bancaResponsible = this.allResponsibles.find(r => r.id === this.currentUser.id);
        if (bancaResponsible) {
          this.selectedResponsible = bancaResponsible;
        this.region.responsibleId = this.currentUser.id;
        }
      }
      
      return true;
    },
    async searchRegions() {
      this.loading = true;
      try {
        const response = await mainService.searchRegions();
        this.regions = response.data;
        this.pageIndex = 1;
        
        // Buscar dados adicionais dos responsáveis
        await this.loadAdditionalRegionData();
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },

    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.pageIndex = newPage;
      }
    },

    async loadAdditionalRegionData() {
      // Buscar dados dos responsáveis para cada região
      const promises = this.regions.map(async (region) => {
        // Buscar dados do responsável principal se existir
        if (region.responsibleId) {
          try {
            const responsibleResponse = await mainService.getUserById(region.responsibleId);
            if (responsibleResponse && responsibleResponse.data) {
              region.responsibleName = responsibleResponse.data.name;
              region.responsibleRole = responsibleResponse.data.role;
            } else {
              region.responsibleName = null;
              region.responsibleRole = null;
            }
          } catch (error) {
            // Se o usuário não existir ou houver erro, apenas definir como null
            region.responsibleName = null;
            region.responsibleRole = null;
            // Não exibir toast para não poluir a interface
          }
        } else {
          region.responsibleName = null;
          region.responsibleRole = null;
        }
      });

      // Usar Promise.allSettled para não falhar se algum usuário não for encontrado
      await Promise.allSettled(promises);
    },
    resolveBancaIdForSave(existingBancaId = null) {
      // Sempre ler do storage no momento do save (não depender só do computed)
      this.syncSelectedBancaSnapshot();

      const fromHeader = this.normalizeGuid(this.selectedBancaSnapshot || this.readSelectedBancaFromStorage());
      const fromResponsible = this.normalizeGuid(
        this.selectedResponsible?.bancaId ?? this.selectedResponsible?.BancaId
      );
      const fromUser = this.normalizeGuid(this.userBancaId);
      const fromExisting = this.normalizeGuid(existingBancaId);

      // ADMIN: banca do cabeçalho é a fonte da verdade do vínculo
      if (this.isAdmin) {
        return fromHeader || fromResponsible || fromExisting || null;
      }

      // BANCA/outros: banca do responsável/usuário primeiro
      return fromResponsible || fromUser || fromHeader || fromExisting || null;
    },

    insertRegion() {
      if (this.saving) return;

      if (this.validateRegion()) {
        // Validar se é BANCA e não tem bancaId
        if (this.isBanca && !this.userBancaId) {
          toast.error("Erro: Usuário BANCA deve ter uma banca associada!");
          return;
        }

        const bancaIdToUse = this.resolveBancaIdForSave();

        // ADMIN precisa vincular a região a uma banca
        if (this.isAdmin && !bancaIdToUse) {
          toast.error("Selecione uma banca no cabeçalho antes de criar a região!");
          return;
        }

        const regionData = {
          name: this.region.name?.trim() || "",
          responsibleId: this.selectedResponsible ? this.selectedResponsible.id : null,
          descargaRegionId: this.selectedDescargaRegion ? this.selectedDescargaRegion.id : null,
          bancaId: bancaIdToUse,
          BancaId: bancaIdToUse, // garantir binding no backend
          onlyDescarga: Boolean(this.onlyDescarga),
          prizeExpirationDays: 7, // Valor padrão
          requirePinForPrizeClaim: true, // Padrão: exige PIN (comportamento atual)
          milharBonusEnabled: false, // Padrão: milhar bônus desativado
          milharBonusMinValue: 0,
          milharBonusMaxBetValue: 10,
          milharBonusCotacao: 100,
          milharBonusMaxPrize: 1000,
          ticketFooterEnabled: false,
          ticketFooterImage: null,
          ticketFooterText: null,
          isActive: true, // Novas regiões são ativas por padrão
          copyFromRegionId: this.copyFromRegionId || null
        };
        
        // Para BANCA, forçar responsável como o próprio usuário
        if (this.isBanca) {
          regionData.responsibleId = this.currentUser.id;
          // Para BANCA, o bancaId é obrigatório
          if (!regionData.bancaId) {
            toast.error("Erro: BancaId é obrigatório para usuários BANCA!");
            return;
          }
        }

        this.saving = true;
        
        mainService
          .insertRegion(regionData)
          .then(async (response) => {
            // Atualizar o regionId no usuário responsável
            if (response && response.data && response.data.id && regionData.responsibleId) {
              await this.updateUserRegionId(regionData.responsibleId, response.data.id);
            }
            
            toast.success(
              this.copyFromRegionId
                ? "Região cadastrada e configurações copiadas com sucesso!"
                : "Região cadastrada com sucesso!"
            );
            this.reset();
            // Recarregar regiões do usuário responsável para usuários não-admin
            if (!this.isAdmin) {
              await this.loadUserResponsibleRegions();
            }
            await this.searchRegions();
            this.notifyHeaderSelectsRefresh({ bancas: false, regions: true });
          })
          .catch((error) => {
            toast.error(handleErrorMessage(error));
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    updateRegion() {
      if (this.saving) return;

      if (this.validateRegion()) {
        // Ao trocar o responsável, trocar também para a banca desse usuário / header.
        let bancaIdToUse = this.resolveBancaIdForSave(this.region.bancaId);
        
        // Se for BANCA e não houver bancaId na região (caso improvável), usar o do usuário
        if (this.isBanca && !bancaIdToUse) {
          bancaIdToUse = this.userBancaId;
        }

        if (this.isAdmin && !bancaIdToUse) {
          toast.error("Não foi possível determinar a banca da região. Selecione uma banca no cabeçalho ou um responsável com banca!");
          return;
        }
        
        const regionData = {
          id: this.region.id,
          name: this.region.name,
          responsibleId: this.selectedResponsible ? this.selectedResponsible.id : null,
          descargaRegionId: this.selectedDescargaRegion ? this.selectedDescargaRegion.id : null,
          bancaId: bancaIdToUse,
          BancaId: bancaIdToUse, // garantir binding no backend
          onlyDescarga: this.onlyDescarga === true, // Garantir boolean explícito
          prizeExpirationDays: this.region.prizeExpirationDays || 7, // Usar valor da região ou padrão de 7 dias
          // Preservar flags; bool omitido no binder vira false
          requirePinForPrizeClaim: this.region.requirePinForPrizeClaim !== false,
          milharBonusEnabled: this.region.milharBonusEnabled === true,
          milharBonusMinValue: Number(this.region.milharBonusMinValue) || 0,
          milharBonusMaxBetValue: Number(this.region.milharBonusMaxBetValue) > 0
            ? Number(this.region.milharBonusMaxBetValue)
            : 10,
          milharBonusCotacao: Number(this.region.milharBonusCotacao) > 0
            ? Number(this.region.milharBonusCotacao)
            : 100,
          milharBonusMaxPrize: Number(this.region.milharBonusMaxPrize) > 0
            ? Number(this.region.milharBonusMaxPrize)
            : 1000,
          ticketFooterEnabled: this.region.ticketFooterEnabled === true,
          ticketFooterImage: this.region.ticketFooterImage || null,
          ticketFooterText: this.region.ticketFooterText || null,
          isActive: this.region.isActive !== undefined ? this.region.isActive : true // Manter estado atual ou ativo por padrão
        };
        
        // Para BANCA, forçar responsável como o próprio usuário
        if (this.isBanca) {
          regionData.responsibleId = this.currentUser.id;
        }

        this.saving = true;
        
        mainService
          .updateRegion(regionData)
          .then(async () => {
            // Detectar mudança de responsável
            const responsibleChanged = this.originalResponsibleId !== regionData.responsibleId;
            
            if (responsibleChanged) {
              // Remover regionId do responsável anterior (se existir)
              if (this.originalResponsibleId) {
                await this.removeUserRegionId(this.originalResponsibleId, this.region.id);
              }
            }
            
            // Atualizar o regionId no novo usuário responsável (se houver)
            if (regionData.responsibleId && this.region.id) {
              await this.updateUserRegionId(regionData.responsibleId, this.region.id);
            }
            
            toast.success("Região atualizada com sucesso!");
            this.reset();
            // Recarregar regiões do usuário responsável para usuários não-admin
            if (!this.isAdmin) {
              await this.loadUserResponsibleRegions();
            }
            await this.searchRegions();
            this.notifyHeaderSelectsRefresh({ bancas: false, regions: true });
          })
          .catch((error) => {
            toast.error(handleErrorMessage(error));
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    deleteRegion(idRegion) {
      if (!this.canDeleteRegions) {
        toast.error("Você não tem permissão para excluir regiões!");
        return;
      }

      this.$confirm({
        message: "Confirma a exclusão?",
        button: { no: "Não", yes: "Sim" },
        callback: (confirm) => {
          if (confirm) {
            mainService
              .deleteRegion(idRegion)
              .then(async () => {
                toast.success("Região excluída com sucesso!");
                // Recarregar regiões do usuário responsável para usuários não-admin
                if (!this.isAdmin) {
                  await this.loadUserResponsibleRegions();
                }
                await this.searchRegions();
                this.notifyHeaderSelectsRefresh({ bancas: false, regions: true });
                this.selectedTab = 1;
              })
              .catch((error) => {
                toast.error(handleErrorMessage(error));
              })
              .finally(() => {});
          }
        },
      });
      this.$nextTick(() => {
        setTimeout(() => {
          const confirmButtons = document.getElementsByClassName("vc-btn left");
          if (confirmButtons && confirmButtons.length > 0) {
            confirmButtons[0].focus();
          }
        }, 100);
      });
    },

    toggleRegionActive(region) {
      const action = region.isActive ? 'desativar' : 'ativar';
      const actionCapitalized = region.isActive ? 'Desativar' : 'Ativar';
      
      this.$confirm({
        message: `Confirma ${action} a região "${region.name}"?`,
        button: { no: "Não", yes: "Sim" },
        callback: (confirm) => {
          if (confirm) {
            mainService
              .toggleRegionActive(region.id)
              .then(async () => {
                toast.success(`Região ${action === 'ativar' ? 'ativada' : 'desativada'} com sucesso!`);
                await this.searchRegions();
                this.notifyHeaderSelectsRefresh({ bancas: false, regions: true });
              })
              .catch((error) => {
                toast.error(handleErrorMessage(error));
              })
              .finally(() => {});
          }
        },
      });
      this.$nextTick(() => {
        setTimeout(() => {
          const confirmButtons = document.getElementsByClassName("vc-btn left");
          if (confirmButtons && confirmButtons.length > 0) {
            confirmButtons[0].focus();
          }
        }, 100);
      });
    },

    async openTransferBancaModal(region) {
      if (!this.isAdmin) {
        toast.error("Você não tem permissão para transferir regiões entre bancas!");
        return;
      }

      this.transferRegion = region;
      this.transferTargetBancaId = "";
      this.showTransferBancaModal = true;

      if (this.transferBancas.length === 0) {
        this.loadingTransferBancas = true;
        try {
          const response = await mainService.searchBancas({ isActive: true });
          this.transferBancas = response.data || [];
        } catch (error) {
          this.transferBancas = [];
          toast.error("Erro ao carregar lista de bancas");
        } finally {
          this.loadingTransferBancas = false;
        }
      }
    },

    closeTransferBancaModal() {
      if (this.transferring) return;
      this.showTransferBancaModal = false;
      this.transferRegion = null;
      this.transferTargetBancaId = "";
    },

    async confirmTransferBanca() {
      if (!this.transferRegion || !this.transferTargetBancaId) {
        toast.error("Selecione a banca de destino!");
        return;
      }

      if (String(this.transferTargetBancaId) === String(this.transferRegion.bancaId)) {
        toast.error("Selecione uma banca diferente da banca atual!");
        return;
      }

      this.transferring = true;
      try {
        const region = this.transferRegion;

        // Se o responsável atual não for ADMIN, ele fica vinculado à banca de origem
        // e não faz mais sentido continuar responsável após a transferência.
        let responsibleId = region.responsibleId || null;
        let clearedResponsibleId = null;

        if (responsibleId) {
          let responsible = this.allResponsibles.find(r => r.id === responsibleId);
          if (!responsible) {
            try {
              const resp = await mainService.getUserById(responsibleId);
              responsible = resp?.data || null;
            } catch {
              responsible = null;
            }
          }

          const role = typeof responsible?.role === 'string'
            ? parseInt(responsible.role)
            : responsible?.role;

          if (role !== ROLES.ADMIN) {
            clearedResponsibleId = responsibleId;
            responsibleId = null;
          }
        }

        const regionData = {
          id: region.id,
          name: region.name,
          responsibleId,
          descargaRegionId: region.descargaRegionId || null,
          bancaId: this.transferTargetBancaId,
          BancaId: this.transferTargetBancaId,
          onlyDescarga: region.onlyDescarga === true,
          prizeExpirationDays: region.prizeExpirationDays || 7,
          requirePinForPrizeClaim: region.requirePinForPrizeClaim !== false,
          milharBonusEnabled: region.milharBonusEnabled === true,
          milharBonusMinValue: Number(region.milharBonusMinValue) || 0,
          milharBonusMaxBetValue: Number(region.milharBonusMaxBetValue) > 0
            ? Number(region.milharBonusMaxBetValue)
            : 10,
          milharBonusCotacao: Number(region.milharBonusCotacao) > 0
            ? Number(region.milharBonusCotacao)
            : 100,
          milharBonusMaxPrize: Number(region.milharBonusMaxPrize) > 0
            ? Number(region.milharBonusMaxPrize)
            : 1000,
          ticketFooterEnabled: region.ticketFooterEnabled === true,
          ticketFooterImage: region.ticketFooterImage || null,
          ticketFooterText: region.ticketFooterText || null,
          isActive: region.isActive !== undefined ? region.isActive : true
        };

        await mainService.updateRegion(regionData);

        if (clearedResponsibleId) {
          await this.removeUserRegionId(clearedResponsibleId, region.id);
        }

        // Vendedores não têm BancaId próprio: seguem a região automaticamente
        // (a banca deles é sempre resolvida via RegionId -> Region.BancaId).
        // Já os cobradores têm vínculo próprio de banca (tabela BancaUser),
        // então precisam ser movidos explicitamente junto com a região.
        const movedCollectorsCount = await this.moveRegionCollectorsToBanca(
          region.id,
          this.transferTargetBancaId,
          clearedResponsibleId
        );

        toast.success(
          movedCollectorsCount > 0
            ? `Região transferida com sucesso! ${movedCollectorsCount} cobrador(es) movido(s) junto para a nova banca.`
            : "Região transferida para a nova banca com sucesso!"
        );
        this.showTransferBancaModal = false;
        this.transferRegion = null;
        this.transferTargetBancaId = "";

        if (!this.isAdmin) {
          await this.loadUserResponsibleRegions();
        }
        await this.searchRegions();
        this.notifyHeaderSelectsRefresh({ bancas: false, regions: true });
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.transferring = false;
      }
    },

    async moveRegionCollectorsToBanca(regionId, newBancaId, skipUserId = null) {
      const collectorIds = new Set();

      // Cobradores atribuídos diretamente à região (User.RegionId)
      try {
        const usersResponse = await mainService.searchUsers({
          regionId,
          role: ROLES.COBRADOR
        });
        (usersResponse?.data || []).forEach(u => collectorIds.add(u.id));
      } catch {
        // Ignorar falha na busca; a transferência da região já foi concluída
      }

      // Cobradores vinculados aos vendedores da região (Seller.CollectorId)
      try {
        const sellersResponse = await mainService.searchSellersByRegion(regionId);
        (sellersResponse?.data || []).forEach(seller => {
          if (seller.collectorId) {
            collectorIds.add(seller.collectorId);
          }
        });
      } catch {
        // Ignorar falha na busca; a transferência da região já foi concluída
      }

      if (skipUserId) {
        collectorIds.delete(skipUserId);
      }

      if (collectorIds.size === 0) {
        return 0;
      }

      const results = await Promise.allSettled(
        Array.from(collectorIds).map(id => this.moveUserToBanca(id, newBancaId))
      );

      return results.filter(r => r.status === 'fulfilled' && r.value === true).length;
    },

    async moveUserToBanca(userId, newBancaId) {
      try {
        const userResponse = await mainService.getUserById(userId);
        if (!userResponse || !userResponse.data) return false;

        const userData = userResponse.data;
        const updatedUser = {
          id: userData.id,
          name: userData.name,
          username: userData.username,
          password: userData.password,
          phone: userData.phone,
          role: userData.role,
          regionId: userData.regionId,
          bancaId: newBancaId,
          isActive: userData.isActive
        };

        await mainService.updateUser(updatedUser);
        return true;
      } catch {
        // Não interromper a transferência da região por causa de um cobrador específico
        return false;
      }
    },

    async loadRegionResponsible(region) {
      this.selectedResponsible = null;
      this.responsibleFilterTerm = "";
      
      if (region.responsibleId) {
        // Primeiro tentar encontrar nos responsáveis já carregados
        let responsible = this.allResponsibles.find(r => r.id === region.responsibleId);
        
        // Se não encontrar, buscar na API
        if (!responsible) {
        try {
          const response = await mainService.getUserById(region.responsibleId);
          
          if (response && response.data) {
              responsible = {
              ...response.data,
              roleText: this.getRoleText(response.data.role)
            };
          }
        } catch (error) {
          // Ignorar erro
        }
        }
        
        if (responsible) {
          this.selectedResponsible = responsible;
          this.responsibleFilterTerm = responsible.name;
        }
      }
    },

    getRoleBadgeClass(role) {
      // Se é string, mapeia diretamente
      if (typeof role === 'string') {
        const roleUpper = role.toUpperCase();
        switch (roleUpper) {
          case 'ADMIN': return 'badge-admin';      // ADMIN - Vermelho
          case 'GERENTE': return 'badge-manager';  // GERENTE - Azul
          case 'COBRADOR': return 'badge-collector'; // COBRADOR - Verde
          case 'BANCA': return 'badge-manager';    // BANCA - Azul (mesma cor do GERENTE)
          default: return 'badge-default';
        }
      }
      
      // Se é número, faz o switch numérico
      switch (role) {
        case 0: return 'badge-admin';      // ADMIN - Vermelho
        case 1: return 'badge-manager';    // GERENTE - Azul
        case 2: return 'badge-collector';  // COBRADOR - Verde
        case 3: return 'badge-manager';    // BANCA - Azul (mesma cor do GERENTE)
        default: return 'badge-default';
      }
    },

    getRoleText(role) {
      // Se já é string, retorna diretamente
      if (typeof role === 'string') {
        const roleUpper = role.toUpperCase();
        switch (roleUpper) {
          case 'ADMIN': return 'ADMIN';
          case 'GERENTE': return 'GERENTE';
          case 'COBRADOR': return 'COBRADOR';
          case 'BANCA': return 'BANCA';
          default: return roleUpper || 'INDEFINIDO';
        }
      }
      
      // Se é número, faz o switch numérico
      switch (role) {
        case 0: return 'ADMIN';
        case 1: return 'GERENTE';
        case 2: return 'COBRADOR';
        case 3: return 'BANCA';
        default: return 'INDEFINIDO';
      }
    },

    // Métodos para modal de vendedores
    async showSellersList(region) {
      this.selectedRegionForSellers = region;
      this.showSellersModal = true;
      this.loadingSellers = true;
      this.sellersList = [];

      try {
        const response = await mainService.searchSellersByRegion(region.id);
        this.sellersList = response.data || [];
      } catch (error) {
        toast.error("Erro ao carregar vendedores da região");
        this.sellersList = [];
      } finally {
        this.loadingSellers = false;
      }
    },

    closeSellersModal() {
      this.showSellersModal = false;
      this.selectedRegionForSellers = null;
      this.sellersList = [];
      this.loadingSellers = false;
    },

    showPermissionError() {
      toast.error("Você não tem permissão para criar/editar regiões!");
    },

    // Métodos para auto-complete de regiões de descarga
    async loadAllDescargaRegions() {
      // Se já carregamos as regiões, não precisa recarregar
      if (this.allDescargaRegions.length > 0) {
        return;
      }

      this.loadingDescargaRegions = true;

      try {
        // Destino da Descarga: buscar todas as regiões do sistema
        // (sem filtro de banca/região do cabeçalho) e manter só as de descarga
        const response = await mainService.searchRegions({});
        const regions = response.data || [];

        this.allDescargaRegions = regions.filter(region =>
          region.onlyDescarga === true
        );
      } catch (error) {
        this.allDescargaRegions = [];
      } finally {
        this.loadingDescargaRegions = false;
      }
    },

    selectDescargaRegion(region) {
      this.selectedDescargaRegion = region;
      this.descargaRegionFilterTerm = region.name;
      this.showDescargaRegionList = false;
      this.region.descargaRegionId = region.id;
    },

    clearDescargaRegion() {
      this.selectedDescargaRegion = null;
      this.descargaRegionFilterTerm = "";
      this.region.descargaRegionId = null;
    },

    hideDescargaRegionList() {
      // Delay para permitir o clique no item
      setTimeout(() => {
        this.showDescargaRegionList = false;
      }, 150);
    },

    async loadRegionDescargaRegion(region) {
      this.selectedDescargaRegion = null;
      this.descargaRegionFilterTerm = "";
      
      if (region.descargaRegionId) {
        // Primeiro tentar encontrar nos responsáveis já carregados
        let descargaRegion = this.allDescargaRegions.find(r => r.id === region.descargaRegionId);
        
        // Se não encontrar, buscar na API
        if (!descargaRegion) {
        try {
          const response = await mainService.getRegionById(region.descargaRegionId);
            if (response && response.data) {
              descargaRegion = response.data;
          }
        } catch (error) {
          // Se não conseguir buscar por ID, tentar buscar pelo nome se existir na região
          if (region.descargaRegionName) {
              descargaRegion = {
              id: region.descargaRegionId,
              name: region.descargaRegionName,
              onlyDescarga: true
            };
            }
          }
        }
        
        if (descargaRegion) {
          this.selectedDescargaRegion = descargaRegion;
          this.descargaRegionFilterTerm = descargaRegion.name;
          this.region.descargaRegionId = descargaRegion.id;
        }
      }
    },

    async updateUserRegionId(userId, regionId) {
      try {
        // Buscar dados completos do usuário
        const userResponse = await mainService.getUserById(userId);
        
        if (userResponse && userResponse.data) {
          const userData = userResponse.data;
          
          // Atualizar o regionId
          const updatedUser = {
            id: userData.id,
            name: userData.name,
            username: userData.username,
            password: userData.password, // Manter senha atual
            phone: userData.phone,
            role: userData.role,
            regionId: regionId, // ATUALIZAR O REGIONID
            bancaId: userData.bancaId,
            isActive: userData.isActive
          };
          
          // Atualizar o usuário
          await mainService.updateUser(updatedUser);
        }
      } catch (error) {
        // Não mostrar toast de erro para não confundir o usuário
        // A região foi criada/atualizada com sucesso, apenas o vínculo no usuário que falhou
      }
    },

    async removeUserRegionId(userId, regionId) {
      try {
        // Buscar dados completos do usuário
        const userResponse = await mainService.getUserById(userId);
        
        if (userResponse && userResponse.data) {
          const userData = userResponse.data;
          
          // Verificar se o regionId do usuário é o mesmo que estamos removendo
          if (userData.regionId === regionId) {
            // Remover o regionId (setando para null)
            const updatedUser = {
              id: userData.id,
              name: userData.name,
              username: userData.username,
              password: userData.password,
              phone: userData.phone,
              role: userData.role,
              regionId: null, // REMOVER O REGIONID
              bancaId: userData.bancaId,
              isActive: userData.isActive
            };
            
            // Atualizar o usuário
            await mainService.updateUser(updatedUser);
          }
        }
      } catch (error) {
        // Ignorar erro
      }
    },
  },
};
</script>

<style scoped>
/* Alinhamento da tabela */
.modern-table {
  width: 100%;
  text-align: center;
}

.modern-table th {
  text-align: center;
  vertical-align: middle;
}

.modern-table td {
  text-align: center;
  vertical-align: middle;
  padding: 12px 8px;
}

.table-count {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 400;
  white-space: nowrap;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-pagination {
  padding: 0.5rem;
  border: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
  color: var(--text-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: var(--transition, all 0.2s);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-pagination:hover:not(:disabled) {
  background: var(--accent-primary);
  color: white;
  border-color: var(--accent-primary);
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: var(--text-primary);
  min-width: 60px;
  text-align: center;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
}

@media (max-width: 768px) {
  .pagination-footer {
    flex-direction: column;
    gap: 1rem;
  }
}

/* Primeira coluna - Nome da Região */
.region-info {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.region-name {
  font-weight: 500;
  color: var(--text-primary);
}

/* Segunda coluna - Responsável */
.seller-column {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  text-align: center;
}

.seller-name {
  display: inline-block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
  color: var(--text-primary);
}

.no-seller {
  color: var(--text-muted);
  font-style: italic;
}

/* Terceira coluna - Tipo de Região */
.region-type-column {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.region-type-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: 600;
  color: white;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.region-type-badge i {
  font-size: 0.9em;
}

.region-type-badge.badge-descarga {
  background: linear-gradient(135deg, var(--success), var(--info));
}

.region-type-badge.badge-normal {
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
}

.region-type-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Quarta coluna - Ações */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

/* Estado vazio */
.no-data-message {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 24px;
  color: var(--text-muted);
}

/* Mensagem de permissão - adaptada para temas */
.permission-info {
    padding: 7px;
    background-color: var(--primary-color);
    border: 1px solid var(--primary-hover);
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 10px;
    color: white;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-content {
  font-size: 0.9em;
  line-height: 1.4;
}

/* Mensagem de informação para BANCA */
.banca-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  padding: 10px;
  background-color: var(--secondary-color);
  border-radius: 6px;
  font-size: 0.85em;
  color: var(--text-muted);
}

.banca-info-warning {
  background-color: rgba(245, 158, 11, 0.12);
  color: var(--text-primary, #92400e);
  border: 1px solid rgba(245, 158, 11, 0.35);
}

/* Aba desabilitada */
.tab-button.disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: none;
}

.tab-button.disabled:hover {
  background-color: var(--bg-secondary) !important;
  color: var(--text-muted) !important;
}

/* Checkbox personalizado */
.checkbox-group {
  margin-top: 10px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  position: relative;
  padding: 12px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.checkbox-container:hover {
  background-color: var(--hover-color);
}

.checkbox-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkbox-checkmark {
  height: 20px;
  width: 20px;
  background-color: var(--card-background);
  border: 2px solid var(--border-color);
  border-radius: 4px;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.checkbox-input:checked ~ .checkbox-checkmark {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.checkbox-checkmark:after {
  content: "";
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-input:checked ~ .checkbox-checkmark:after {
  display: block;
}

.checkbox-label {
  font-weight: 500;
  color: var(--text-primary);
  user-select: none;
}

.checkbox-description {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: var(--primary-color-10);
  border-radius: 6px;
  font-size: 0.85em;
  color: var(--info-color);
}

.checkbox-description i {
  flex-shrink: 0;
}

/* Espaçamento adicional entre input-groups */
.input-group-spaced {
  margin-top: 2rem;
}

/* Estilos para o componente de região selecionada - compatível com temas */
.selected-region {
  margin-top: 1rem;
}

.region-card {
  background: var(--secondary-color);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
}

.region-card:hover {
  background: var(--hover-color);
  border-color: var(--accent-color);
}

.region-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--accent-color), var(--accent-hover));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.region-details {
  flex: 1;
}

.region-details h4 {
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.region-details p {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0 0 0.5rem 0;
}

/* Badge de tipo de região dentro do card */
.region-details .region-type-badge {
  background: var(--accent-color);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Estilos para info nos dropdowns de região - compatível com temas */
.autocomplete-item .region-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: flex-start;
}

.autocomplete-item .region-name {
  font-weight: 500;
  color: var(--text-primary);
}

.autocomplete-item .region-type {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-style: italic;
}

/* Descrição de campo - adaptada para temas */
.field-description {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: var(--primary-color-10);
  border-radius: 6px;
  font-size: 0.85em;
  color: var(--info-color);
}

.field-description i {
  flex-shrink: 0;
}

/* Coluna de Status */
.status-column {
  display: flex;
  justify-content: center;
  align-items: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: 600;
  color: white;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.status-badge i {
  font-size: 0.9em;
}

.status-badge.badge-active {
  background: linear-gradient(135deg, #28a745, #20c997);
}

.status-badge.badge-inactive {
  background: linear-gradient(135deg, #dc3545, #c82333);
}

.status-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Botão de ativar/desativar */
.btn-action.btn-warning {
  background: linear-gradient(135deg, #ffc107, #ff9800);
  color: white;
}

.btn-action.btn-warning:hover {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
}

.btn-action.btn-success {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
}

.btn-action.btn-success:hover {
  background: linear-gradient(135deg, #20c997, #17a2b8);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
}

/* Botão de transferir banca */
.btn-action.btn-transfer {
  background: linear-gradient(135deg, #6f42c1, #8e5ef7);
  color: white;
}

.btn-action.btn-transfer:hover {
  background: linear-gradient(135deg, #5a32a3, #6f42c1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(111, 66, 193, 0.4);
}

/* Mensagem informativa no autocomplete */
.autocomplete-info {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 193, 7, 0.05) 100%);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  padding: 12px 15px;
  margin-top: 5px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-primary);
  font-size: 0.9rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.autocomplete-info i {
  color: #ffc107;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.autocomplete-info strong {
  color: var(--accent-primary, #007bff);
  font-weight: 600;
}

[data-theme="dark"] .autocomplete-info {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 193, 7, 0.08) 100%);
  border-color: rgba(255, 193, 7, 0.4);
}

/* Mensagem de ajuda no autocomplete */
.autocomplete-help {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, rgba(23, 162, 184, 0.1) 0%, rgba(23, 162, 184, 0.05) 100%);
  border: 1px solid rgba(23, 162, 184, 0.3);
  border-radius: 8px;
  padding: 12px 15px;
  margin-top: 5px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-primary);
  font-size: 0.9rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.autocomplete-help i {
  color: #17a2b8;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.autocomplete-help strong {
  color: #17a2b8;
  font-weight: 600;
}

[data-theme="dark"] .autocomplete-help {
  background: linear-gradient(135deg, rgba(23, 162, 184, 0.15) 0%, rgba(23, 162, 184, 0.08) 100%);
  border-color: rgba(23, 162, 184, 0.4);
}

/* Melhorar visibilidade do botão clear */
.clear-selection-btn {
  background: rgba(220, 53, 69, 0.1);
  border-radius: 50%;
  transition: all 0.2s;
}

.clear-selection-btn:hover {
  background: rgba(220, 53, 69, 0.2);
  transform: scale(1.1);
}

/* Evitar que dropdowns fiquem cortados pelo form-card (overflow:hidden global) */
.regions-container,
.tab-content,
.form-card-with-dropdowns,
.form-card-with-dropdowns.dropdown-open,
.form-card-with-dropdowns .form-body,
.form-card-with-dropdowns .input-group,
.form-card-with-dropdowns .form-footer {
  overflow: visible !important;
}

.form-card-with-dropdowns .input-group {
  position: relative;
  z-index: 1;
}

.form-card-with-dropdowns .input-group.dropdown-field-open {
  z-index: 50;
}

/* Select com filtro de responsáveis */
.select-with-filter-container {
  position: relative;
  overflow: visible;
  z-index: 1;
}

.select-with-filter-container.is-open {
  z-index: 100;
}

.filter-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.filter-icon {
  position: absolute;
  left: 12px;
  color: var(--text-muted);
  pointer-events: none;
  z-index: 1;
}

.filter-input {
  padding-left: 40px;
  width: 100%;
}

.responsibles-list {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--card-background) !important;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);
  max-height: 240px;
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 2000;
  opacity: 1;
}

.list-loading,
.list-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px;
  color: var(--text-muted);
  font-size: 0.9rem;
}

.list-loading i {
  color: var(--primary-color);
}

.responsibles-items {
  padding: 4px;
}

.responsible-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  border: 1px solid transparent;
  min-height: 0;
}

.responsible-item:hover {
  background: var(--hover-color);
  border-color: var(--primary-color);
}

.responsible-item.selected {
  background: var(--primary-color-10);
  border-color: var(--primary-color);
}

.responsible-avatar {
  width: 26px;
  height: 26px;
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.responsible-info {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  overflow: hidden;
}

.responsible-name {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.responsible-details {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}

.responsible-username {
  font-size: 0.78rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.responsible-role-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  color: white;
  flex-shrink: 0;
  line-height: 1.3;
}

.responsible-role-badge.badge-admin {
  background: linear-gradient(135deg, #dc3545, #c82333);
}

.responsible-role-badge.badge-manager {
  background: linear-gradient(135deg, #007bff, #0056b3);
}

.responsible-role-badge.badge-collector {
  background: linear-gradient(135deg, #28a745, #218838);
}

.responsible-role-badge.badge-default {
  background: linear-gradient(135deg, #6c757d, #5a6268);
}

.responsible-role-badge.badge-descarga {
  background: linear-gradient(135deg, #28a745, #20c997);
}

.responsible-role-badge.badge-normal {
  background: linear-gradient(135deg, #007bff, #0056b3);
}

.check-icon {
  color: var(--success);
  font-size: 1.2rem;
  flex-shrink: 0;
}

.selected-responsible-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding: 12px 15px;
  background: var(--primary-color-10);
  border: 1px solid var(--primary-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-weight: 500;
}

.selected-responsible-preview i:first-child {
  color: var(--primary-color);
  font-size: 1.1rem;
}

.responsible-role-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: var(--primary-color);
  color: white;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: auto;
}

.responsible-role-tag.badge-descarga {
  background: linear-gradient(135deg, #28a745, #20c997);
}

.responsible-role-tag.badge-normal {
  background: linear-gradient(135deg, #007bff, #0056b3);
}

.clear-responsible-btn {
  background: transparent;
  border: none;
  color: var(--danger);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 5px;
}

.clear-responsible-btn:hover {
  background: rgba(220, 53, 69, 0.1);
  transform: scale(1.1);
}

.clear-responsible-btn i {
  font-size: 0.9rem;
}
</style>


